import styled from '@emotion/styled';
import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  ALL_STYLE_CATEGORY_IDS,
  ALL_VISUAL_CATEGORIES,
  applyStyle,
  captureStyle,
  getKindProfile,
  STYLE_CATEGORIES,
} from 'modules/manager/domain/style-clipboard';
import type { StyleCategoryId } from 'modules/manager/domain/style-clipboard';
import { useStyleClipboard } from 'modules/manager/ui/hooks';
import React, { useCallback, useState } from 'react';
import { editorAccent } from '../../../editor-accent';
import InspectorSection from '../shared/inspector-section';

interface StyleClipboardSectionProps {
  editor: FabricEditor;
  active: fabric.Object[];
}

const StyleClipboardSection: React.FC<StyleClipboardSectionProps> = ({ editor, active }) => {
  const { captured, setCaptured, clear } = useStyleClipboard();
  const [selectiveOpen, setSelectiveOpen] = useState(false);
  // Remembered across pastes within the session; size is opt-in only.
  const [selected, setSelected] = useState<StyleCategoryId[]>(ALL_VISUAL_CATEGORIES);

  const toggleCategory = useCallback((id: StyleCategoryId) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }, []);

  if (!captured && active.length === 0) return null;

  const capturedCategories = captured ? STYLE_CATEGORIES.filter((c) => captured.values[c.id]) : [];

  // A category is applicable when at least one selected target's profile can
  // accept at least one captured key from it.
  const isApplicable = (id: StyleCategoryId): boolean => {
    const bucket = captured?.values[id];
    if (!bucket) return false;
    return active.some((target) => {
      const fields = getKindProfile(target)?.[id];
      return Boolean(fields?.some((f) => f.key in bucket));
    });
  };

  const canCopy = active.length === 1;
  const canPaste = Boolean(captured) && active.length >= 1;
  const applicableSelected = selected.filter(isApplicable);

  const handleCopy = () => {
    if (!canCopy) return;
    setCaptured(captureStyle(active[0]));
  };

  const handlePasteAll = () => {
    if (!captured || !canPaste) return;
    applyStyle(editor, active, captured, ALL_STYLE_CATEGORY_IDS);
  };

  const handlePasteSelective = () => {
    if (!captured || !canPaste || applicableSelected.length === 0) return;
    applyStyle(editor, active, captured, applicableSelected);
  };

  return (
    <InspectorSection title="Буфер стилей">
      {captured ? (
        <Chip>
          <ChipHeader>
            <ChipTitle title={captured.sourceKind}>В буфере: {captured.sourceLabel}</ChipTitle>
            <ClearButton type="button" title="Очистить буфер" onClick={clear}>
              ×
            </ClearButton>
          </ChipHeader>
          <Tags>
            {capturedCategories.map((c) => (
              <Tag key={c.id}>{c.label}</Tag>
            ))}
          </Tags>
        </Chip>
      ) : (
        <Empty>Буфер пуст</Empty>
      )}

      <Button type="button" disabled={!canCopy} onClick={handleCopy} title="Ctrl/⌘+Shift+C">
        Скопировать стиль
      </Button>
      <Button type="button" disabled={!canPaste} onClick={handlePasteAll} title="Ctrl/⌘+Shift+V">
        Вставить стиль
      </Button>

      {captured && (
        <ToggleButton type="button" onClick={() => setSelectiveOpen((v) => !v)}>
          {selectiveOpen ? 'Скрыть выборочную вставку' : 'Вставить выборочно…'}
        </ToggleButton>
      )}

      {captured && selectiveOpen && (
        <Checklist>
          {capturedCategories.map((c) => {
            const applicable = isApplicable(c.id);
            return (
              <CheckRow key={c.id} className={applicable ? '' : 'opacity-50'}>
                <input
                  type="checkbox"
                  disabled={!applicable}
                  checked={selected.includes(c.id)}
                  onChange={() => toggleCategory(c.id)}
                />
                <span>{c.label}</span>
              </CheckRow>
            );
          })}
          <Button
            type="button"
            disabled={!canPaste || applicableSelected.length === 0}
            onClick={handlePasteSelective}
          >
            Применить
          </Button>
        </Checklist>
      )}
    </InspectorSection>
  );
};

export default StyleClipboardSection;

const Chip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
`;

const ChipHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const ChipTitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClearButton = styled.button`
  flex: none;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    color: #111827;
    background: #e5e7eb;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Tag = styled.span`
  font-size: 10px;
  color: #374151;
  background: #e5e7eb;
  border-radius: 9999px;
  padding: 2px 8px;
`;

const Empty = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const Button = styled.button`
  width: 100%;
  padding: 6px 8px;
  font-size: 12px;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
    background: #f9fafb;
  }
`;

const ToggleButton = styled.button`
  align-self: flex-start;
  padding: 0;
  font-size: 12px;
  color: ${editorAccent.ink};
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    color: ${editorAccent.inkHover};
    text-decoration: underline;
  }
`;

const Checklist = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
`;

const CheckRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
`;
